"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";

import { Link } from "@/navigation";

interface MaterialCard {
	keyPrefix: string;
	image: string;
}

interface IndustryMaterialsProps {
	namespace: string;
	materials: readonly MaterialCard[];
	brandColor?: string;
}

export default function IndustryMaterials({
	namespace,
	materials,
	brandColor = "#64a525",
}: IndustryMaterialsProps) {
	const t = useTranslations(namespace);
	const desktopGridCols = materials.length === 4 ? "lg:grid-cols-4" : "lg:grid-cols-3";
	const imageSizes =
		materials.length === 4
			? "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
			: "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw";

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<header className="mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t("materialsTitle")}
					</h2>
					<p className="text-sm leading-relaxed text-black md:text-base">{t("materialsDesc")}</p>
				</header>

				<div className={`grid grid-cols-1 gap-6 sm:grid-cols-2 ${desktopGridCols}`}>
					{materials.map((mat) => (
						<div key={mat.keyPrefix} className="flex h-full flex-col shadow-card">
							<div className="relative aspect-[4/2.2] w-full overflow-hidden">
								<Image
									src={mat.image}
									alt={t(`${mat.keyPrefix}Alt`)}
									fill
									className="object-cover"
									sizes={imageSizes}
								/>
							</div>
							<div className="flex flex-1 flex-col bg-[#fcfff4] p-4">
								<h3 className="mb-2 text-lg font-bold text-black sm:text-xl">{t(`${mat.keyPrefix}Title`)}</h3>
								<p className="text-sm leading-relaxed text-black">{t(`${mat.keyPrefix}Desc`)}</p>
							</div>
						</div>
					))}
				</div>

				<div className="mt-8 flex justify-center sm:mt-10 md:mt-12">
					<Link
						href="/materials"
						className="inline-block rounded px-8 py-3 text-base font-semibold text-white transition-opacity hover:text-white hover:opacity-90"
						style={{ backgroundColor: brandColor }}
					>
						{t("materialsCtaBtn")}
					</Link>
				</div>
			</div>
		</section>
	);
}
